import {
  queryOptions,
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";
import { toModifiedProcessModelId } from "@/api/identifiers";
import { qk } from "@/api/query-keys";

export type StartApplicationInput = {
  readonly processModelId: string;
};

export type StartedApplication = {
  readonly caseId: number;
  /** 起案フォームのタスク。見つからない場合は null。 */
  readonly taskGuid: string | null;
};

/** RJSF に渡すフォーム定義（backend が返す JSON Schema をそのまま使う）。 */
export type TaskForm = {
  readonly schema: Readonly<Record<string, unknown>>;
  readonly uiSchema: Readonly<Record<string, unknown>>;
  readonly formData: Readonly<Record<string, unknown>>;
  /** タスクの表示名（どの入力画面か分かるように出す）。 */
  readonly taskTitle: string | null;
  /**
   * このタスクが属するモデル id。プロセス全体の図を出すのに必要
   * （図の取得は modified id をパスに使うため）。
   */
  readonly processModelId: string | null;
};

export type TaskRef = {
  readonly caseId: number;
  readonly taskGuid: string;
};

/**
 * 起票の失敗。画面が理由を出し分けられるよう、どの手順で失敗したかと、作れた案件を持つ。
 */
export class StartApplicationError extends Error {
  /**
   * どの手順で失敗したか。
   * - `create`: 案件の作成（POST /process-instances）。案件はできていない
   * - `run`: 実行（POST /process-instance-run）。案件はできている
   * - `tasks`: 起案タスクの取得（GET /tasks）。案件は作成・実行とも済んでいる
   */
  readonly stage: "create" | "run" | "tasks";
  /** 作れた案件（作成より後の手順で失敗したときだけ）。 */
  readonly caseId: number | null;
  override readonly cause: unknown;

  constructor(
    stage: StartApplicationError["stage"],
    caseId: number | null,
    cause: unknown,
  ) {
    super(`application start failed at ${stage}`);
    this.name = "StartApplicationError";
    this.stage = stage;
    this.caseId = caseId;
    this.cause = cause;
  }
}

/**
 * 起票を開始する。SpiffArena には「フォームを先に見せてから作る」API が無いため、
 * インスタンスを作って実行し、開いた最初のタスク（起案フォーム）を拾う。
 *
 * 作成より後で失敗しても instance は残る。実行で失敗すると「エラー」（モデルの設定により
 * 一時停止）として一覧に残り、起案タスクの取得で失敗すると起案タスクが開いたまま残る。
 * 押し直すと 2 件目ができるので、画面は作れた案件を示して押し直させない（describeStartError）。
 */
export async function startApplication(
  client: ApiClient,
  input: StartApplicationInput,
): Promise<StartedApplication> {
  const modifiedId = toModifiedProcessModelId(input.processModelId);

  const created = await client
    .post<{ id: number }>(`/process-instances/${modifiedId}`)
    .catch((cause: unknown) => {
      throw new StartApplicationError("create", null, cause);
    });
  // 本番の CloudFront は 403 / 404 を 200 の index.html に差し替える。id の無い応答を
  // 成功として進めると「案件 #undefined」や無言の成功になるので、ここで止める
  if (typeof (created as { id?: unknown } | null)?.id !== "number") {
    throw new StartApplicationError(
      "create",
      null,
      new Error("unexpected response from POST /process-instances"),
    );
  }
  await client
    .post(`/process-instance-run/${modifiedId}/${created.id}`)
    .catch((cause: unknown) => {
      throw new StartApplicationError("run", created.id, cause);
    });

  const tasks = await client
    .get<{
      readonly results?: readonly {
        readonly process_instance_id?: number;
        readonly task_guid?: string | null;
        readonly id?: string | null;
      }[];
    }>("/tasks", { query: { per_page: 100, page: 1 } })
    .catch((cause: unknown) => {
      throw new StartApplicationError("tasks", created.id, cause);
    });

  const mine = (tasks.results ?? []).find(
    (task) => task.process_instance_id === created.id,
  );

  return {
    caseId: created.id,
    taskGuid: mine?.task_guid ?? mine?.id ?? null,
  };
}

/** タスクのフォーム定義と初期値。下書き（saved_form_data）があればそれを初期値にする。 */
export function taskFormQueryOptions(client: ApiClient, ref: TaskRef) {
  const fetchForm = async (): Promise<TaskForm> => {
    const task = await client.get<{
      readonly form_schema?: Record<string, unknown> | null;
      readonly form_ui_schema?: Record<string, unknown> | null;
      readonly data?: Record<string, unknown> | null;
      readonly saved_form_data?: Record<string, unknown> | null;
      readonly task_title?: string | null;
      readonly process_model_identifier?: string | null;
    }>(`/tasks/${ref.caseId}/${ref.taskGuid}`, {
      query: { with_form_data: true },
    });

    return {
      schema: task.form_schema ?? {},
      uiSchema: task.form_ui_schema ?? {},
      formData: task.saved_form_data ?? task.data ?? {},
      taskTitle: emptyToNull(task.task_title),
      processModelId: emptyToNull(task.process_model_identifier),
    };
  };

  return queryOptions({
    queryKey: qk.tasks.form(ref.caseId, ref.taskGuid),
    queryFn: fetchForm,
    // フォーム定義はタスクごとに固定。開いている間は取り直さない。
    staleTime: Infinity,
  });
}

export function useTaskForm(ref: TaskRef | null): UseQueryResult<TaskForm> {
  const client = useApiClient();
  return useQuery({
    ...taskFormQueryOptions(client, ref ?? { caseId: 0, taskGuid: "" }),
    enabled: ref !== null,
  });
}

/** タスクを提出する（起案の場合はここで案件が動き出す）。 */
export function useSubmitTask(): UseMutationResult<
  unknown,
  Error,
  { readonly ref: TaskRef; readonly formData: Record<string, unknown> }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ref, formData }) =>
      client.put(`/tasks/${ref.caseId}/${ref.taskGuid}`, formData),
    onSuccess: async () => {
      // 一覧・タスク数が変わるので両方捨てる
      await queryClient.invalidateQueries({
        queryKey: qk.processInstances._root(),
      });
      await queryClient.invalidateQueries({ queryKey: qk.tasks._root() });
    },
  });
}

/** 下書き保存（提出せずに内容だけ残す）。 */
export function useSaveDraft(): UseMutationResult<
  unknown,
  Error,
  { readonly ref: TaskRef; readonly formData: Record<string, unknown> }
> {
  const client = useApiClient();

  return useMutation({
    mutationFn: ({ ref, formData }) =>
      client.post(`/tasks/${ref.caseId}/${ref.taskGuid}/save-draft`, formData),
  });
}

/** 空文字は「未設定」として扱う（backend は空文字を返すことがある）。 */
function emptyToNull(value: string | null | undefined): string | null {
  return value === null || value === undefined || value === "" ? null : value;
}
